import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Upload, Camera, FileText, FileSpreadsheet, Sparkles, Loader2, ArrowLeft, ArrowRight, Check,
  Trash2, AlertTriangle, Stethoscope, Send, Plus, Printer,
} from "lucide-react";

const SERVICE_TYPES = ["service", "mot", "diagnostic", "repair", "inspection", "other"];

const blankAppt = () => ({
  time: "",
  customer_name: "",
  phone: "",
  email: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_year: null,
  vehicle_reg: "",
  service_type: "other",
  work_description: "",
  // Toyota DMS extras (passed through if present)
  wip_number: "",
  out_time: "",
  skill_hours: "",
  order_status: "",
  loan_car: "",
  sales_exec: "",
  advisor_name: "",
  campaign_number: "",
  home_phone: "",
});

// =====================================================================
// Step 1 — Upload (CSV or photo)
// =====================================================================
function StepUpload({ date, setDate, busy, error, onUpload }) {
  const [tab, setTab] = useState("excel");
  return (
    <div className="p-6 space-y-5">
      <div>
        <label className="label-overline">Appointment date <span className="text-muted-foreground normal-case font-normal">(optional)</span></label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="input-default text-sm mt-1 w-fit"
          data-testid="diary-wizard-date"
        />
        <p className="text-[11px] text-muted-foreground mt-1">Leave blank to default to <strong>tomorrow</strong>. AI may detect a date from the photo — that wins.</p>
      </div>

      <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/40 border border-border w-fit overflow-x-auto max-w-full">
        <button
          type="button"
          onClick={() => setTab("excel")}
          className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors whitespace-nowrap ${tab === "excel" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="diary-wizard-tab-excel"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" /> Excel <span className="text-[9px] uppercase font-mono-plex opacity-70">DMS</span>
        </button>
        <button
          type="button"
          onClick={() => setTab("photo")}
          className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors whitespace-nowrap ${tab === "photo" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="diary-wizard-tab-photo"
        >
          <Camera className="w-3.5 h-3.5" /> Photo
        </button>
        <button
          type="button"
          onClick={() => setTab("csv")}
          className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors whitespace-nowrap ${tab === "csv" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="diary-wizard-tab-csv"
        >
          <FileText className="w-3.5 h-3.5" /> CSV
        </button>
      </div>

      {tab === "excel" ? (
        <div className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/[0.03] p-6 text-center">
          <FileSpreadsheet className="w-10 h-10 text-primary mx-auto" />
          <div className="font-semibold mt-3">Drop tomorrow's diary export <span className="ml-1 text-[10px] uppercase tracking-wider text-primary font-mono-plex">recommended</span></div>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
            Export the Diary Detail from your DMS as <strong>.xlsx</strong> — we read every column instantly. No AI, no waiting, no misreads.
          </p>
          <p className="text-[10px] text-muted-foreground/80 mt-2">
            Toyota / Kerridge / Pinewood / DragonDMS / RTC ➜ all supported.
          </p>
          <input
            id="diary-wiz-excel"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0], "excel")}
            data-testid="diary-wizard-excel-input"
          />
          <label htmlFor="diary-wiz-excel" className={`btn-primary text-sm mt-4 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "Parsing…" : "Upload Excel"}
          </label>
        </div>
      ) : tab === "photo" ? (
        <div className="rounded-lg border-2 border-dashed border-border p-6 text-center">
          <Sparkles className="w-10 h-10 text-primary mx-auto" />
          <div className="font-semibold mt-3">Snap your printed diary</div>
          <p className="text-xs text-muted-foreground mt-1">Our AI reads the page and pulls every booking — works with phone photos and PDFs. Best for paper-only days.</p>
          <input
            id="diary-wiz-photo"
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0], "photo")}
            data-testid="diary-wizard-photo-input"
          />
          <label htmlFor="diary-wiz-photo" className={`btn-primary text-sm mt-4 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {busy ? "AI reading the page…" : "Take photo / upload"}
          </label>
          <p className="text-[11px] text-muted-foreground mt-3">Reading takes ~10–20 seconds per page.</p>
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-border p-6 text-center">
          <FileText className="w-10 h-10 text-muted-foreground mx-auto" />
          <div className="font-semibold mt-3">Drop your diary CSV</div>
          <p className="text-xs text-muted-foreground mt-1">Columns we recognise: time, customer, phone, email, make, model, year, reg, work, WIP, status, skill, loan</p>
          <input
            id="diary-wiz-csv"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0], "csv")}
            data-testid="diary-wizard-csv-input"
          />
          <label htmlFor="diary-wiz-csv" className={`btn-primary text-sm mt-4 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload CSV
          </label>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/30 p-3 text-sm flex items-start gap-2" data-testid="diary-wizard-error">
          <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Step 2 — Review (edit grid)
// =====================================================================
function ApptRow({ row, idx, onChange, onDelete }) {
  const set = (k, v) => onChange(idx, { ...row, [k]: v });
  const isDiag = (row.service_type || "").toLowerCase() === "diagnostic";
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2" data-testid={`diary-review-row-${idx}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <input
          type="time"
          value={row.time || ""}
          onChange={(e) => set("time", e.target.value)}
          className="input-default text-sm w-[110px] font-mono-plex"
          data-testid={`diary-row-time-${idx}`}
        />
        <input
          type="text"
          value={row.customer_name || ""}
          onChange={(e) => set("customer_name", e.target.value)}
          placeholder="Customer name"
          className="input-default text-sm flex-1 min-w-[160px]"
          data-testid={`diary-row-name-${idx}`}
        />
        <input
          type="text"
          value={row.vehicle_reg || ""}
          onChange={(e) => set("vehicle_reg", (e.target.value || "").toUpperCase())}
          placeholder="REG"
          className="input-default text-sm w-[110px] font-mono-plex uppercase"
          data-testid={`diary-row-reg-${idx}`}
        />
        <button
          type="button"
          onClick={() => onDelete(idx)}
          className="btn-outline text-xs text-destructive hover:bg-destructive/10"
          aria-label="Remove row"
          data-testid={`diary-row-delete-${idx}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="tel"
          value={row.phone || ""}
          onChange={(e) => set("phone", e.target.value)}
          placeholder="Phone (07…)"
          className="input-default text-sm"
          data-testid={`diary-row-phone-${idx}`}
        />
        <input
          type="email"
          value={row.email || ""}
          onChange={(e) => set("email", e.target.value)}
          placeholder="email@example.co.uk"
          className="input-default text-sm"
          data-testid={`diary-row-email-${idx}`}
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input
          type="text"
          value={row.vehicle_make || ""}
          onChange={(e) => set("vehicle_make", e.target.value)}
          placeholder="Make"
          className="input-default text-sm"
          data-testid={`diary-row-make-${idx}`}
        />
        <input
          type="text"
          value={row.vehicle_model || ""}
          onChange={(e) => set("vehicle_model", e.target.value)}
          placeholder="Model"
          className="input-default text-sm"
          data-testid={`diary-row-model-${idx}`}
        />
        <input
          type="number"
          value={row.vehicle_year ?? ""}
          onChange={(e) => set("vehicle_year", e.target.value ? Number(e.target.value) : null)}
          placeholder="Year"
          className="input-default text-sm"
          data-testid={`diary-row-year-${idx}`}
        />
        <select
          value={row.service_type || "other"}
          onChange={(e) => set("service_type", e.target.value)}
          className="input-default text-sm"
          data-testid={`diary-row-svc-${idx}`}
        >
          {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <textarea
        value={row.work_description || ""}
        onChange={(e) => set("work_description", e.target.value)}
        placeholder="Work / job description"
        rows={2}
        className="input-default text-sm w-full"
        data-testid={`diary-row-work-${idx}`}
      />
      {/* DMS detail badges — only show what was extracted */}
      {(row.wip_number || row.skill_hours || row.order_status || row.loan_car || row.out_time) && (
        <div className="flex flex-wrap gap-1.5 text-[10px]" data-testid={`diary-row-dms-${idx}`}>
          {row.wip_number && (
            <span className="px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary font-mono-plex">WIP {row.wip_number}</span>
          )}
          {row.out_time && (
            <span className="px-2 py-0.5 rounded-full bg-muted/40 border border-border text-muted-foreground font-mono-plex">OUT {row.out_time}</span>
          )}
          {row.skill_hours && (
            <span className="px-2 py-0.5 rounded-full bg-muted/40 border border-border text-muted-foreground">{row.skill_hours}h skill</span>
          )}
          {row.order_status && (
            <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
              row.order_status.toLowerCase().includes("unconfirmed")
                ? "bg-warning/15 border border-warning/40 text-warning"
                : row.order_status.toLowerCase().includes("waiting")
                  ? "bg-primary/15 border border-primary/40 text-primary"
                  : "bg-success/15 border border-success/40 text-success"
            }`}>{row.order_status}</span>
          )}
          {row.loan_car && (
            <span className="px-2 py-0.5 rounded-full bg-warning/10 border border-warning/30 text-warning">🔑 Loan {row.loan_car}</span>
          )}
        </div>
      )}
      {isDiag && (
        <div className="text-[11px] text-warning inline-flex items-center gap-1">
          <Stethoscope className="w-3 h-3" /> Diagnostic questionnaire will be auto-enabled
        </div>
      )}
    </div>
  );
}

function StepReview({ appts, setAppts, date, setDate, onBack, busy, onCommit }) {
  const updateRow = (idx, next) => setAppts(appts.map((a, i) => (i === idx ? next : a)));
  const deleteRow = (idx) => setAppts(appts.filter((_, i) => i !== idx));
  const addRow = () => setAppts([...appts, blankAppt()]);
  const validCount = appts.filter((a) => (a.customer_name || "").trim() || (a.vehicle_reg || "").trim()).length;

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="label-overline text-primary">Step 2 — Review &amp; fix</div>
          <h3 className="font-display font-bold text-lg mt-1">{appts.length} booking{appts.length === 1 ? "" : "s"} detected</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Tidy any AI misreads (wrong digit, missing email) before we create the jobs.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-muted-foreground">For date</label>
          <input
            type="date"
            value={date || ""}
            onChange={(e) => setDate(e.target.value)}
            className="input-default text-sm"
            data-testid="diary-review-date"
          />
        </div>
      </div>

      <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="diary-review-list">
        {appts.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No bookings yet. Add one below or go back and try a clearer photo.
          </div>
        ) : (
          appts.map((row, idx) => (
            <ApptRow key={idx} row={row} idx={idx} onChange={updateRow} onDelete={deleteRow} />
          ))
        )}
      </div>

      <button type="button" onClick={addRow} className="btn-outline text-xs" data-testid="diary-review-add">
        <Plus className="w-3.5 h-3.5" /> Add booking
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
        <button type="button" onClick={onBack} className="btn-outline text-sm" disabled={busy} data-testid="diary-review-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          type="button"
          onClick={() => onCommit(false)}
          disabled={busy || validCount === 0}
          className="btn-primary text-sm"
          data-testid="diary-review-create-only"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Create {validCount} job{validCount === 1 ? "" : "s"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Step 3 — Done summary
// =====================================================================
function StepDone({ result, onClose, date, onFirePreCalls, busy }) {
  const pc = result.pre_calls;
  const printHref = `/diary/print${date ? `?date=${date}` : ""}`;
  const preCallsSent = !!pc && !pc.error;
  return (
    <div className="p-6 space-y-4" data-testid="diary-wizard-done">
      {/* Created banner */}
      <div className="rounded-lg bg-success/10 border border-success/30 p-4">
        <div className="font-display font-bold text-lg flex items-center gap-2">
          <Check className="w-5 h-5 text-success" />
          Created {result.created} job{result.created === 1 ? "" : "s"}
          {result.skipped ? <span className="text-sm text-muted-foreground font-normal">· skipped {result.skipped} duplicate{result.skipped === 1 ? "" : "s"}</span> : null}
        </div>
        {result.jobs?.some((j) => j.diagnostic) && (
          <div className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
            <Stethoscope className="w-3 h-3" /> Diagnostic questionnaire auto-enabled where the booking mentioned diagnostics.
          </div>
        )}
      </div>

      {/* Next steps header */}
      <div>
        <div className="label-overline text-primary">Next steps</div>
        <p className="text-xs text-muted-foreground mt-1">Pick one or both — the order doesn't matter.</p>
      </div>

      {/* Action 1 — Send pre-calls */}
      <div className={`rounded-lg border p-4 transition-colors ${preCallsSent ? "border-success/40 bg-success/5" : "border-primary/40 bg-primary/[0.04]"}`} data-testid="diary-action-precalls">
        <div className="flex items-start gap-3">
          <span className={`grid place-items-center w-9 h-9 rounded-lg shrink-0 ${preCallsSent ? "bg-success/15 text-success" : "bg-primary/15 text-primary"}`}>
            {preCallsSent ? <Check className="w-5 h-5" /> : <Send className="w-5 h-5" />}
          </span>
          <div className="flex-1">
            <div className="font-display font-bold text-sm">1 · Send pre-calls</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Personalised SMS + email to every customer with the booking details, what to expect on the day, and (for diagnostics) the 10-question form.
            </div>
            {preCallsSent ? (
              <div className="text-xs text-success mt-2 flex flex-wrap items-center gap-3" data-testid="diary-action-precalls-result">
                {pc.sent > 0 && <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" /> {pc.sent} sent</span>}
                {pc.skipped > 0 && <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" /> {pc.skipped} already done</span>}
                {pc.failed > 0 && <span className="inline-flex items-center gap-1 text-destructive"><AlertTriangle className="w-3 h-3" /> {pc.failed} failed (no phone/email)</span>}
              </div>
            ) : pc?.error ? (
              <div className="text-xs text-destructive mt-2">Auto-send failed: {pc.error}</div>
            ) : (
              <button
                type="button"
                onClick={onFirePreCalls}
                disabled={busy}
                className="btn-primary text-xs mt-3"
                data-testid="diary-action-precalls-fire-btn"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send pre-calls now ({result.created} customer{result.created === 1 ? "" : "s"})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Action 2 — Advisor worksheet */}
      <a
        href={printHref}
        target="_blank"
        rel="noreferrer"
        className="block rounded-lg border border-warning/40 bg-warning/[0.04] p-4 hover:bg-warning/[0.08] transition-colors"
        data-testid="diary-action-worksheet"
      >
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-warning/15 text-warning shrink-0">
            <Printer className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <div className="font-display font-bold text-sm flex items-center justify-between gap-2">
              <span>2 · Create advisor worksheet</span>
              <ArrowRight className="w-4 h-4 text-warning shrink-0" />
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Printable A4 landscape run sheet — Time, WIP, Reg, Customer, Work plus tickboxes for VHC, Updated, Invoiced, Collected. Auto colour-coded by job type. Opens ready to print.
            </div>
          </div>
        </div>
      </a>

      <div className="flex justify-end pt-2">
        <button type="button" onClick={onClose} className="btn-outline text-sm" data-testid="diary-wizard-close-done">
          Close
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Wizard shell
// =====================================================================
export default function DiaryImportWizard({ open, onClose, onImported }) {
  const [step, setStep] = useState("upload"); // upload | review | done
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [appts, setAppts] = useState([]);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep("upload"); setDate(""); setBusy(false); setError("");
    setAppts([]); setResult(null);
  };

  const close = () => { reset(); onClose?.(); };

  const handleUpload = async (file, source) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source);
      if (date) fd.append("date", date);
      const r = await api.post("/diary/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const list = (r.data.appointments || []).map((a) => ({ ...blankAppt(), ...a }));
      setAppts(list);
      if (r.data.date) setDate(r.data.date);
      setStep("review");
      toast.success(`Found ${list.length} booking${list.length === 1 ? "" : "s"} — review and fix any mistakes`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't read the file");
      toast.error(e?.response?.data?.detail || "Couldn't read the file");
    } finally { setBusy(false); }
  };

  const handleCommit = async (autoSend) => {
    setBusy(true);
    try {
      const r = await api.post("/diary/import-commit", {
        date: date || null,
        auto_send_pre_calls: !!autoSend,
        appointments: appts,
      });
      setResult(r.data);
      setStep("done");
      onImported?.();
      const parts = [`${r.data.created} created`];
      if (autoSend && r.data.pre_calls?.sent) parts.push(`${r.data.pre_calls.sent} pre-calls sent`);
      toast.success(parts.join(" · "));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const handleFirePreCalls = async () => {
    if (!result?.jobs?.length) return;
    setBusy(true);
    try {
      const ids = result.jobs.map((j) => j.id);
      const r = await api.post("/jobs/bulk-pre-call", {
        job_ids: ids,
        channels: ["sms", "email"],
        skip_already_sent: true,
      });
      setResult((prev) => ({ ...(prev || {}), pre_calls: r.data }));
      onImported?.();
      const parts = [];
      if (r.data.sent) parts.push(`${r.data.sent} sent`);
      if (r.data.skipped) parts.push(`${r.data.skipped} already done`);
      if (r.data.failed) parts.push(`${r.data.failed} failed`);
      toast.success(`Pre-calls: ${parts.join(" · ") || "queued"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send pre-calls");
      setResult((prev) => ({ ...(prev || {}), pre_calls: { error: e?.response?.data?.detail || "Send failed" } }));
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card rounded-lg border border-border shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto animate-fade-up"
        data-testid="diary-wizard"
      >
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="label-overline">Diary import · AI assisted</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Tomorrow's bookings</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              {step === "upload" && "Snap a photo of your daily diary — our AI pulls every booking. CSV upload also supported."}
              {step === "review" && "Review what the AI read. Edit any field, then create the jobs."}
              {step === "done" && "Jobs created. Now pick your next steps."}
            </p>
          </div>
          <button onClick={close} className="btn-outline" data-testid="diary-wizard-close"><X className="w-4 h-4" /></button>
        </div>

        {/* Progress dots */}
        <div className="px-6 pt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
          {[
            { id: "upload", label: "Upload" },
            { id: "review", label: "Review" },
            { id: "done", label: "Done" },
          ].map((s, i, arr) => (
            <span key={s.id} className="flex items-center gap-2">
              <span className={`grid place-items-center w-5 h-5 rounded-full text-[10px] font-mono-plex font-bold ${step === s.id ? "bg-primary text-primary-foreground" : (i < arr.findIndex((x) => x.id === step)) ? "bg-success text-success-foreground" : "bg-muted text-muted-foreground"}`}>
                {i + 1}
              </span>
              <span className={step === s.id ? "font-semibold text-foreground" : ""}>{s.label}</span>
              {i < arr.length - 1 && <span className="text-muted-foreground/40">›</span>}
            </span>
          ))}
        </div>

        {step === "upload" && (
          <StepUpload date={date} setDate={setDate} busy={busy} error={error} onUpload={handleUpload} />
        )}
        {step === "review" && (
          <StepReview appts={appts} setAppts={setAppts} date={date} setDate={setDate} onBack={() => setStep("upload")} busy={busy} onCommit={handleCommit} />
        )}
        {step === "done" && result && (
          <StepDone result={result} onClose={close} date={date} onFirePreCalls={handleFirePreCalls} busy={busy} />
        )}
      </div>
    </div>
  );
}
